import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, User, Plus, ChevronsUpDown, Check } from "lucide-react";

// A bulletproof, plain-React searchable dropdown (no cmdk/Popover) so it works
// reliably inside a Radix Dialog. Clicks always propagate to onPick.
function Combo({
  label, Icon, valueLabel, placeholder, disabled, items, selectedId,
  onPick, onAddNew, addLabel, testid, optPrefix, searchPlaceholder,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const filtered = items.filter((it) => it.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <Label className="text-xs">{label}</Label>
      <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={testid}
        onClick={() => setOpen((o) => !o)} className="w-full justify-between font-normal">
        <span className="flex items-center gap-1.5 truncate">
          <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="truncate">{valueLabel || placeholder}</span>
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </Button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="p-2 border-b border-slate-100">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              data-testid={`${testid}-search`} placeholder={searchPlaceholder} className="h-8 text-sm" />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No match.</div>}
            {filtered.map((it) => (
              <button type="button" key={it.id} data-testid={`${optPrefix}-${it.id}`}
                onClick={() => { onPick(it); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2">
                <Check className={`w-3.5 h-3.5 shrink-0 ${selectedId === it.id ? "opacity-100 text-[#003E87]" : "opacity-0"}`} />
                <span className="truncate">{it.label}</span>
              </button>
            ))}
          </div>
          <button type="button" data-testid={`${optPrefix}-add-new`}
            onClick={() => { setOpen(false); setQ(""); onAddNew(); }}
            className="w-full text-left px-3 py-2 text-sm text-[#003E87] font-medium border-t border-slate-100 hover:bg-slate-50 flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> {addLabel}
          </button>
        </div>
      )}
    </div>
  );
}

// Reusable "build catalogs as we go" picker: choose an existing client (and one of its
// saved contacts) or add a new one — which persists straight into the shared catalog.
export function ClientContactPicker({
  clientId, clientName, contactId, contactName, onClient, onContact, contactOnly = false,
}) {
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [addClient, setAddClient] = useState(false);
  const [addContact, setAddContact] = useState(false);

  // The client universe = saved client docs UNION every client name carried on a bid/lead.
  useEffect(() => { api.get("/clients/pickable").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  // Contacts for the chosen client = saved contacts + people discovered from their emails (merged).
  useEffect(() => {
    if (!clientName) { setContacts([]); return; }
    api.get("/client-contacts", { params: { name: clientName } }).then((r) => setContacts(r.data || [])).catch(() => setContacts([]));
  }, [clientName]);

  const pickClient = (c) => {
    onClient({ client_id: c.client_id || null, name: c.name });
    onContact({ contact_id: null, name: "", email: "", phone: "" });
  };

  return (
    <div className={contactOnly ? "grid grid-cols-1 gap-3" : "grid grid-cols-2 gap-3"}>
      {!contactOnly && <Combo label="Client" Icon={Building2} testid="client-picker-trigger" optPrefix="client-opt"
        valueLabel={clientName} placeholder="Select a client"
        searchPlaceholder="Search clients…" selectedId={clientName} addLabel="Add new client"
        items={clients.map((c) => ({ id: c.name, label: c.name }))}
        onPick={(it) => pickClient(clients.find((c) => c.name === it.id) || { name: it.id })}
        onAddNew={() => setAddClient(true)} />}
      <Combo label="Primary contact" Icon={User} testid="contact-picker-trigger" optPrefix="contact-opt"
        disabled={!clientName} valueLabel={contactName}
        placeholder={clientName ? "Select a contact" : "Pick a client first"}
        searchPlaceholder="Search contacts…" selectedId={contactId} addLabel="Add new contact"
        items={contacts.map((ct) => ({ id: ct.contact_id, label: ct.name + (ct.role ? ` · ${ct.role}` : "") + (ct.touches ? ` · ${ct.touches} email${ct.touches === 1 ? "" : "s"}` : "") }))}
        onPick={(it) => { const ct = contacts.find((c) => c.contact_id === it.id); if (ct) onContact({ contact_id: ct.contact_id, name: ct.name, email: ct.email || "", phone: ct.phone || "" }); }}
        onAddNew={() => setAddContact(true)} />

      <AddClientDialog open={addClient} onOpenChange={setAddClient}
        onCreated={(c) => { setClients((p) => [...p, { name: c.name, client_id: c.client_id }]); pickClient({ name: c.name, client_id: c.client_id }); }} />
      <AddContactDialog open={addContact} onOpenChange={setAddContact} clientName={clientName}
        onCreated={(ct) => { setContacts((p) => [...p, ct]); onContact({ contact_id: ct.contact_id, name: ct.name, email: ct.email || "", phone: ct.phone || "" }); }} />
    </div>
  );
}

function AddClientDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", phone: "" }); }, [open]);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/clients", { name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null });
      toast.success("Client added to your catalog");
      onCreated(res.data); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add client"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-client-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add new client</DialogTitle>
          <DialogDescription>Saved straight to your shared client catalog.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Company name *</Label>
            <Input data-testid="new-client-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Acme Solar EPC" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email</Label>
              <Input data-testid="new-client-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div><Label className="text-xs">Phone</Label>
              <Input data-testid="new-client-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-client-save" onClick={save} disabled={saving} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Add client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddContactDialog({ open, onOpenChange, clientName, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", phone: "", role: "" }); }, [open]);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Contact name is required"); return; }
    if (!clientName) { toast.error("Pick a client first"); return; }
    setSaving(true);
    try {
      const res = await api.post("/client-contacts", {
        client_name: clientName, name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null, role: f.role.trim() || null });
      toast.success("Contact saved");
      onCreated(res.data.contact); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add contact"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-contact-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add contact{clientName ? ` — ${clientName}` : ""}</DialogTitle>
          <DialogDescription>Saved to this client so you can reuse it on new bids and leads.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name *</Label>
              <Input data-testid="new-contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></div>
            <div><Label className="text-xs">Role / title</Label>
              <Input data-testid="new-contact-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="e.g. Primary, PM" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email</Label>
              <Input data-testid="new-contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div><Label className="text-xs">Phone</Label>
              <Input data-testid="new-contact-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-contact-save" onClick={save} disabled={saving} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Save contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
